import { useState } from "react"
import { Badge, Button, Col, Container, Form, Row, Table } from "react-bootstrap"
import { navigateTo } from "../../../redux/router/routerActions"
import { useAppDispatch } from "../../../redux/store"
import { useAddNewAmenityMutation, useGetAmenitiesQuery } from "../../../services/bClientApi"
import { showError, showSuccess } from "../../../redux/counter/counterSlice"
import { AmenityType } from "../../../Types/amenityTypes"

interface ParsedAmenity {
    title: string
    slug: string
    description: string
    tags: string[]
    featured: boolean
    type: string
    isActive: boolean
    image: string
    icon: string
    basePrice: number
    baseQuantity: number
}

interface ImportResult {
    slug: string
    status: 'success' | 'error' | 'skipped'
    message?: string
}

function parseTSV(text: string): ParsedAmenity[] {
    const lines = text.trim().split('\n')
    if (lines.length < 2) return []

    const headers = lines[0].split('\t').map((h) => h.trim())

    return lines
        .slice(1)
        .map((line) => {
            const values = line.split('\t').map((v) => v.trim())
            const row: Record<string, string> = {}
            headers.forEach((header, idx) => {
                row[header] = values[idx] || ''
            })

            const tags = row.tags
                ? row.tags.split(',').map((t) => t.trim()).filter(Boolean)
                : []

            const featured =
                row.featured === 'true' || row.featured === 'yes' || row.featured === '1'

            return {
                title: row.title || '',
                slug: row.slug || '',
                description: row.description || '',
                tags,
                featured,
                type: row.type || 'included',
                isActive: true,
                image: '',
                icon: '',
                basePrice: 0,
                baseQuantity: 0,
            }
        })
        .filter((a) => a.title && a.slug)
}

export default function ImportAmenitiesPage() {
    const dispatch = useAppDispatch()

    const [rawText, setRawText] = useState('')
    const [parsedAmenities, setParsedAmenities] = useState<ParsedAmenity[]>([])
    const [importing, setImporting] = useState(false)
    const [results, setResults] = useState<ImportResult[]>([])

    const { data: existingAmenitiesData } = useGetAmenitiesQuery(
        {
            amenitiesQuery: {},
            pagination: { page: 1, limit: 500 },
        },
        { skip: parsedAmenities.length === 0 }
    )

    const [addNewAmenity] = useAddNewAmenityMutation()

    const existingSlugs = new Set(
        (existingAmenitiesData?.docs || []).map((a: AmenityType) => a.slug)
    )

    const handleParse = () => {
        const parsed = parseTSV(rawText)
        setParsedAmenities(parsed)
        setResults([])
    }

    const handleImport = async () => {
        setImporting(true)
        const importResults: ImportResult[] = []

        for (const amenity of parsedAmenities) {
            if (existingSlugs.has(amenity.slug)) {
                importResults.push({ slug: amenity.slug, status: 'skipped', message: 'Slug already exists' })
                continue
            }
            try {
                await addNewAmenity(amenity).unwrap()
                importResults.push({ slug: amenity.slug, status: 'success' })
            } catch (err: any) {
                importResults.push({
                    slug: amenity.slug,
                    status: 'error',
                    message: err?.data?.message || 'Unknown error',
                })
            }
        }

        setResults(importResults)
        setImporting(false)

        const successCount = importResults.filter((r) => r.status === 'success').length
        const errorCount = importResults.filter((r) => r.status === 'error').length

        if (successCount > 0) {
            dispatch(showSuccess(`${successCount} amenit${successCount === 1 ? 'y' : 'ies'} imported`))
        }
        if (errorCount > 0) {
            dispatch(showError(`${errorCount} amenit${errorCount === 1 ? 'y' : 'ies'} failed to import`))
        }
    }

    const newCount = parsedAmenities.filter((a) => !existingSlugs.has(a.slug)).length
    const existingCount = parsedAmenities.filter((a) => existingSlugs.has(a.slug)).length

    return (
        <Container>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <h1>Import Amenities</h1>
                        <Button variant="secondary" onClick={() => dispatch(navigateTo('/amenities'))}>
                            Back to Amenities
                        </Button>
                    </div>
                    <hr />

                    <p className="text-muted">
                        Paste a tab-separated list of amenities below. The first row should be headers:{' '}
                        <code>title</code>, <code>slug</code>, <code>description</code>, <code>tags</code>,{' '}
                        <code>featured</code>, <code>type</code>
                    </p>
                    <p className="text-muted small">
                        Tags should be comma-separated. Only amenities with slugs that do not already exist will be created.
                    </p>

                    <Form.Group className="mb-3">
                        <Form.Label>Tab-Separated Data</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={10}
                            value={rawText}
                            onChange={(e) => setRawText(e.target.value)}
                            placeholder={"title\tslug\tdescription\ttags\tfeatured\ttype\nWashing Machine\twashing-machine\t\tlaundry\t\tincluded"}
                            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                        />
                    </Form.Group>

                    <Button
                        variant="primary"
                        onClick={handleParse}
                        disabled={!rawText.trim()}
                        className="mb-4"
                    >
                        Parse
                    </Button>

                    {parsedAmenities.length > 0 && (
                        <>
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <h4 className="mb-0">Preview</h4>
                                <Badge bg="success">{newCount} new</Badge>
                                {existingCount > 0 && (
                                    <Badge bg="secondary">{existingCount} already exist</Badge>
                                )}
                            </div>

                            <Table striped bordered hover responsive size="sm" className="mb-3">
                                <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Title</th>
                                        <th>Slug</th>
                                        <th>Tags</th>
                                        <th>Type</th>
                                        <th>Featured</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {parsedAmenities.map((amenity, idx) => {
                                        const exists = existingSlugs.has(amenity.slug)
                                        const result = results.find((r) => r.slug === amenity.slug)
                                        return (
                                            <tr key={idx} className={exists ? 'table-secondary' : ''}>
                                                <td>
                                                    {result ? (
                                                        <Badge
                                                            bg={
                                                                result.status === 'success'
                                                                    ? 'success'
                                                                    : result.status === 'skipped'
                                                                    ? 'secondary'
                                                                    : 'danger'
                                                            }
                                                        >
                                                            {result.status}
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg={exists ? 'secondary' : 'success'}>
                                                            {exists ? 'exists' : 'new'}
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td>{amenity.title}</td>
                                                <td>
                                                    <code>{amenity.slug}</code>
                                                </td>
                                                <td>{amenity.tags.join(', ')}</td>
                                                <td>{amenity.type}</td>
                                                <td>{amenity.featured ? 'Yes' : 'No'}</td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </Table>

                            {newCount > 0 && results.length === 0 && (
                                <Button
                                    variant="success"
                                    onClick={handleImport}
                                    disabled={importing}
                                >
                                    {importing
                                        ? 'Importing...'
                                        : `Import ${newCount} New Amenit${newCount === 1 ? 'y' : 'ies'}`}
                                </Button>
                            )}

                            {results.length > 0 && (
                                <div className="alert alert-info">
                                    Import complete: {results.filter((r) => r.status === 'success').length} created,{' '}
                                    {results.filter((r) => r.status === 'skipped').length} skipped,{' '}
                                    {results.filter((r) => r.status === 'error').length} errors.
                                </div>
                            )}
                        </>
                    )}
                </Col>
            </Row>
        </Container>
    )
}
